import { useRef, useState, type FormEvent, type MouseEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { insertLink, isWebUrl, wrapSelection, type FormattedText } from '../markdown-format';
import { DISCLAIMER_MAX_LENGTH } from '../types';
import { GHOST_FOCUS_RING, TEXTAREA_BOX_FOCUS_RING } from '../ui-chrome';
import { BoldIcon, ItalicIcon, LinkIcon, ToolbarDivider } from './settings-icons';

export const DISCLAIMER_PLACEHOLDER =
	'e.g. This chat is provided by Kim on behalf of Kahawa 1893. Replies may be generated by AI, and a person may not always be available. By chatting, you agree to our Privacy Policy.';

interface DisclaimerTextFieldProps {
	id: string;
	value: string;
	onChange: (value: string) => void;
}

// Figma draws the toolbar icons in text/quote (#4e5258).
const TOOL_BUTTON = cn('size-6 rounded-[4px] text-text-quote hover:bg-gray-100 hover:text-text-quote', GHOST_FOCUS_RING);

const MARKERS = [
	{ label: 'Bold', Icon: BoldIcon, marker: '**' },
	{ label: 'Italic', Icon: ItalicIcon, marker: '*' }
] as const;

// Keeps the textarea focused, so its selection is still there when the click lands.
const keepFocus = (event: MouseEvent) => event.preventDefault();

/**
 * The disclaimer's text field (Figma 6828:45251): the text over a bold / italic / link toolbar.
 * It stores markdown, which is what the widget renders, so the toolbar wraps the selection in
 * markdown rather than styling it in place.
 */
export const DisclaimerTextField = ({ id, value, onChange }: DisclaimerTextFieldProps) => {
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const [isLinkOpen, setLinkOpen] = useState(false);
	const [linkUrl, setLinkUrl] = useState('');

	const selection = () => {
		const textarea = textareaRef.current;
		return { start: textarea?.selectionStart ?? value.length, end: textarea?.selectionEnd ?? value.length };
	};

	const apply = ({ text, selection: next }: FormattedText) => {
		onChange(text);
		// After React has put the new text in, so the selection lands on it.
		requestAnimationFrame(() => {
			textareaRef.current?.focus();
			textareaRef.current?.setSelectionRange(next.start, next.end);
		});
	};

	const addLink = (event: FormEvent) => {
		event.preventDefault();
		if (!isWebUrl(linkUrl)) return;
		apply(insertLink(value, selection(), linkUrl));
		setLinkUrl('');
		setLinkOpen(false);
	};

	return (
		<div
			className={cn(
				'flex min-h-20 w-full flex-col gap-8 rounded-2xl border border-gray-500 bg-white p-3',
				TEXTAREA_BOX_FOCUS_RING
			)}
		>
			<textarea
				id={id}
				ref={textareaRef}
				value={value}
				maxLength={DISCLAIMER_MAX_LENGTH}
				placeholder={DISCLAIMER_PLACEHOLDER}
				rows={2}
				onChange={(event) => onChange(event.target.value)}
				className='min-h-8 w-full resize-none border-0 bg-transparent p-0 text-xs leading-4 text-ink outline-none placeholder:text-gray-800'
			/>
			<div className='flex items-center gap-2'>
				<div className='flex items-center gap-1'>
					{MARKERS.map(({ label, Icon, marker }) => (
						<Button
							key={label}
							type='button'
							variant='ghost'
							size='icon-sm'
							aria-label={label}
							className={TOOL_BUTTON}
							onMouseDown={keepFocus}
							onClick={() => apply(wrapSelection(value, selection(), marker))}
						>
							<Icon className='size-3.5' />
						</Button>
					))}
				</div>
				<ToolbarDivider className='text-gray-400' />
				<Popover open={isLinkOpen} onOpenChange={setLinkOpen}>
					<PopoverTrigger asChild>
						<Button
							type='button'
							variant='ghost'
							size='icon-sm'
							aria-label='Link'
							className={TOOL_BUTTON}
							onMouseDown={keepFocus}
						>
							<LinkIcon className='size-3.5' />
						</Button>
					</PopoverTrigger>
					<PopoverContent align='start' className='w-72 p-2'>
						<form onSubmit={addLink} className='flex items-center gap-2'>
							<Input
								autoFocus
								aria-label='Link URL'
								placeholder='https://'
								value={linkUrl}
								onChange={(event) => setLinkUrl(event.target.value)}
								className='h-8 text-xs'
							/>
							<Button
								type='submit'
								disabled={!isWebUrl(linkUrl)}
								className='h-8 border-0 bg-ink px-3 text-xs font-normal text-white hover:bg-ink'
							>
								Add
							</Button>
						</form>
					</PopoverContent>
				</Popover>
			</div>
		</div>
	);
};
